import {Http} from '../../../Data/config/Http';
import {API_ROUTES} from '../../../constants/apiRoutes';
import {RoleFilters, AssignRolePermissionRequest, BulkAssignRolePermissionsRequest} from '../models/role.model';
import {RoleDTO, RolesListResponseDTO} from '../mappers/role.mapper';
import {PermissionDTO, PermissionsListResponseDTO, UserEffectivePermissionsDTO} from '../mappers/permission.mapper';
import {
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionFilters,
  AssignUserPermissionRequest,
  DenyUserPermissionRequest
} from '../models/permission.model';
import {
  CreateUserDTO,
  UpdateUserDTO,
  UpdatePasswordDTO,
  AssignRoleDTO,
  UsersListResponseDTO,
  UserStatisticsDTO,
  UserDTO,
  ApiResponseDTO
} from '../models/user.dto';
import {UserFilters, UserStatus} from '../models/user.model';
import {handleDataSourceError} from '../../../utils/errorHelpers';

interface RoleStatistics {
  total: number;
  systemRoles: number;
  customRoles: number;
  roleDistribution: Array<{ role: string; count: number }>;
}

interface PermissionStatistics {
  total: number;
  byResource: Array<{ resource: string; count: number }>;
  byAction: Array<{ action: string; count: number }>;
}

export class UserAPIDataSource {
  /**
   * Uses centralized Http client (baseURL, auth, refresh, error normalization).
   */
  private async makeRequest<T> (endpoint: string, options: { method?: string; body?: string } = {}): Promise<T> {
    try {
      const method = (options.method || 'GET').toUpperCase();
      if (method === 'GET') {
        const response = await Http.get<T>(endpoint);
        return response.data;
      }
      if (method === 'POST') {
        const body = options.body ? JSON.parse(options.body) : undefined;
        const response = await Http.post<T>(endpoint, body);
        return response.data;
      }
      if (method === 'PUT') {
        const body = options.body ? JSON.parse(options.body) : undefined;
        const response = await Http.put<T>(endpoint, body);
        return response.data;
      }
      if (method === 'DELETE') {
        const body = options.body ? JSON.parse(options.body) : undefined;
        const response = await Http.delete<T>(endpoint, body !== undefined ? {data: body} : {});
        return response.data;
      }
      throw new Error(`Unsupported method: ${method}`);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // User Management Endpoints
  async createUser (userData: CreateUserDTO): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(
      API_ROUTES.USERS.REGISTER,
      {
        method: 'POST',
        body: JSON.stringify(userData)
      }
    );
    return response.data;
  }

  async getUsers (filters?: UserFilters): Promise<UsersListResponseDTO> {
    const params = new URLSearchParams();

    if (filters?.status) {params.append('status', filters.status);}
    if (filters?.roleId) {params.append('role_id', filters.roleId.toString());}
    if (filters?.search) {params.append('search', filters.search);}
    if (filters?.page) {params.append('page', filters.page.toString());}
    if (filters?.limit !== undefined) {params.append('limit', filters.limit.toString());}

    const queryString = params.toString();
    const endpoint = queryString ? `${API_ROUTES.USERS.BASE}?${queryString}` : API_ROUTES.USERS.BASE;

    const response = await this.makeRequest<UsersListResponseDTO>(endpoint);
    return response;
  }

  async getAdmins (): Promise<UsersListResponseDTO> {
    const response = await this.makeRequest<UsersListResponseDTO>(API_ROUTES.USERS.ADMINS);
    return response;
  }

  async getUserById (id: number): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(API_ROUTES.USERS.BY_ID(id.toString()));

    // Handle case where API returns null data
    if (response.data === null) {
      throw new Error(`User with ID ${id} not found`);
    }

    return response.data;
  }

  /**
   * Updates user profile and handles null data response
   * Some APIs return successful response with null data, so we fetch fresh data
   * Always fetch fresh user data after update to ensure we have complete information including role
   */
  async updateUserProfile (id: number, userData: UpdateUserDTO): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(
      API_ROUTES.USERS.PROFILE(id.toString()),
      {
        method: 'PUT',
        body: JSON.stringify(userData)
      }
    );

    // Always fetch fresh user data after update to ensure we have complete information including role
    // This ensures the role information is always included in the response
    try {
      const freshUserData = await this.getUserById(id);
      return freshUserData;
    } catch (fetchError) {
      // If fetching fails, try to use the response data if available
      if (response.data !== null) {
        return response.data;
      }
      // If both fail, re-throw the fetch error
      throw fetchError;
    }
  }

  async updateUserPassword (id: number, passwordData: UpdatePasswordDTO): Promise<{ message: string }> {
    const response = await this.makeRequest<{ message: string }>(
      API_ROUTES.USERS.PASSWORD(id.toString()),
      {
        method: 'PUT',
        body: JSON.stringify(passwordData)
      }
    );
    return response;
  }

  async assignRole (id: number, roleData: AssignRoleDTO): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(
      API_ROUTES.USERS.ROLES(id.toString()),
      {
        method: 'POST',
        body: JSON.stringify(roleData)
      }
    );

    // Handle case where API returns null data for successful role assignment
    if (response.data === null) {
      // Fetch the user data after role assignment to get complete information
      try {
        const userResponse = await this.makeRequest<ApiResponseDTO<UserDTO>>(
          API_ROUTES.USERS.BY_ID(id.toString()),
          {
            method: 'GET'
          }
        );
        return userResponse.data;
      } catch (fetchError) {
        // If fetching user data fails, return a minimal DTO
        return {
          id: id,
          username: '',
          email: '',
          status: 'active' as UserStatus,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    }

    return response.data;
  }

  async getUserStatistics (): Promise<UserStatisticsDTO> {
    const response = await this.makeRequest<{ data: UserStatisticsDTO }>(API_ROUTES.USERS.STATISTICS);
    return response.data;
  }

  // Permission Management Endpoints (New RBAC System)
  async createPermission (permissionData: CreatePermissionRequest): Promise<PermissionDTO> {
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO>>(
      API_ROUTES.PERMISSIONS.BASE,
      {
        method: 'POST',
        body: JSON.stringify(permissionData)
      }
    );
    return response.data;
  }

  async getPermissions (filters?: PermissionFilters): Promise<PermissionsListResponseDTO> {
    const params = new URLSearchParams();

    if (filters?.resource) {params.append('resource', filters.resource);}
    if (filters?.action) {params.append('action', filters.action);}
    if (filters?.page) {params.append('page', filters.page.toString());}
    if (filters?.limit !== undefined) {params.append('limit', filters.limit.toString());}
    // Cache-busting: ensures fresh data after permission edits
    params.append('_t', Date.now().toString());

    const endpoint = `${API_ROUTES.PERMISSIONS.BASE}?${params.toString()}`;
    const response = await this.makeRequest<ApiResponseDTO<PermissionsListResponseDTO>>(endpoint);
    return response.data;
  }

  async updatePermission (id: number, permissionData: UpdatePermissionRequest): Promise<PermissionDTO> {
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO>>(
      API_ROUTES.PERMISSIONS.BY_ID(id.toString()),
      {
        method: 'PUT',
        body: JSON.stringify(permissionData)
      }
    );
    return response.data;
  }

  async deletePermission (id: number): Promise<{ message: string }> {
    const response = await this.makeRequest<{ message: string }>(
      API_ROUTES.PERMISSIONS.BY_ID(id.toString()),
      {
        method: 'DELETE'
      }
    );
    return response;
  }

  // User Permission Overrides
  async assignUserPermission (data: AssignUserPermissionRequest): Promise<PermissionDTO> {
    const apiData = {
      user_id: data.userId,
      permission_id: data.permissionId,
      granted_by: data.grantedBy,
      expires_at: data.expiresAt,
      reason: data.reason
    };
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO>>(
      API_ROUTES.PERMISSIONS.USERS.ASSIGN,
      {
        method: 'POST',
        body: JSON.stringify(apiData)
      }
    );
    return response.data;
  }

  async denyUserPermission (data: DenyUserPermissionRequest): Promise<PermissionDTO> {
    const apiData = {
      user_id: data.userId,
      permission_id: data.permissionId,
      granted_by: data.grantedBy,
      reason: data.reason
    };
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO>>(
      API_ROUTES.PERMISSIONS.USERS.DENY,
      {
        method: 'POST',
        body: JSON.stringify(apiData)
      }
    );
    return response.data;
  }

  async getUserEffectivePermissions (userId: number): Promise<UserEffectivePermissionsDTO> {
    // Use the correct user endpoint to get user with permissions
    const response = await this.makeRequest<ApiResponseDTO<UserEffectivePermissionsDTO>>(
      API_ROUTES.USERS.BY_ID(userId.toString())
    );
    return response.data;
  }

  // Delete user
  async deleteUser (id: number): Promise<{ message: string }> {
    const response = await this.makeRequest<ApiResponseDTO<{ message: string }>>(
      API_ROUTES.USERS.DELETE(id.toString()),
      {
        method: 'DELETE'
      }
    );

    // Handle case where API returns null data for successful deletion
    if (response.data === null) {
      return {message: 'User deleted successfully'};
    }

    return response.data;
  }

  // Activate/Deactivate user
  async activateUser (id: number): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(
      API_ROUTES.USERS.ACTIVATE(id.toString()),
      {
        method: 'PUT'
      }
    );

    // Handle case where API returns null data for successful activation
    if (response.data === null) {
      // Fetch the user data after activation to get complete information
      try {
        const userResponse = await this.makeRequest<ApiResponseDTO<UserDTO>>(
          API_ROUTES.USERS.BY_ID(id.toString()),
          {
            method: 'GET'
          }
        );
        return userResponse.data;
      } catch (fetchError) {
        // If fetching user data fails, return a minimal DTO with the activated status
        return {
          id: id,
          username: '',
          email: '',
          status: 'active' as UserStatus,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    }

    return response.data;
  }

  async deactivateUser (id: number): Promise<UserDTO> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO>>(
      API_ROUTES.USERS.DEACTIVATE(id.toString()),
      {
        method: 'PUT'
      }
    );

    // Handle case where API returns null data for successful deactivation
    if (response.data === null) {
      // Fetch the user data after deactivation to get complete information
      try {
        const userResponse = await this.makeRequest<ApiResponseDTO<UserDTO>>(
          API_ROUTES.USERS.BY_ID(id.toString()),
          {
            method: 'GET'
          }
        );
        return userResponse.data;
      } catch (fetchError) {
        // If fetching user data fails, return a minimal DTO with the deactivated status
        return {
          id: id,
          username: '',
          email: '',
          status: 'inactive' as UserStatus,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    }

    return response.data;
  }

  // Role Management Methods
  async getRoles (filters?: RoleFilters): Promise<RolesListResponseDTO> {
    const params = new URLSearchParams();

    if (filters?.name) {params.append('name', filters.name);}
    if (filters?.isSystem !== undefined) {params.append('isSystem', filters.isSystem.toString());}
    if (filters?.page) {params.append('page', filters.page.toString());}
    if (filters?.limit !== undefined) {params.append('limit', filters.limit.toString());}

    const queryString = params.toString();
    const endpoint = queryString ? `${API_ROUTES.ROLES.BASE}?${queryString}` : API_ROUTES.ROLES.BASE;

    return this.makeRequest<RolesListResponseDTO>(endpoint);
  }

  async getRoleById (id: number): Promise<RoleDTO> {
    const response = await this.makeRequest<ApiResponseDTO<RoleDTO>>(
      API_ROUTES.ROLES.BY_ID(id.toString()),
      {method: 'GET'}
    );
    return response.data;
  }

  async createRole (roleData: {
    name: string;
    display_name: string;
    description: string;
    permissions: string[];
    is_system: boolean;
  }): Promise<RoleDTO> {
    const response = await this.makeRequest<ApiResponseDTO<RoleDTO>>(
      API_ROUTES.ROLES.BASE,
      {
        method: 'POST',
        body: JSON.stringify(roleData)
      }
    );
    return response.data;
  }

  async deleteRole (id: number): Promise<void> {
    await this.makeRequest<void>(
      API_ROUTES.ROLES.BY_ID(id.toString()),
      {method: 'DELETE'}
    );
  }

  async assignRolePermission (data: AssignRolePermissionRequest): Promise<unknown> {
    const apiData = {
      permission_id: data.permissionId,
      granted_by: data.grantedBy,
      reason: 'Permission assignment'
    };
    const response = await this.makeRequest<ApiResponseDTO<unknown>>(
      API_ROUTES.ROLES.PERMISSIONS.ASSIGN(data.roleId.toString()),
      {
        method: 'POST',
        body: JSON.stringify(apiData)
      }
    );
    return response.data;
  }

  async bulkAssignRolePermissions (data: BulkAssignRolePermissionsRequest): Promise<unknown> {
    const apiData = {
      permission_ids: data.permissionIds,
      granted_by: data.grantedBy,
      reason: 'Bulk permission assignment'
    };
    const response = await this.makeRequest<ApiResponseDTO<unknown>>(
      API_ROUTES.ROLES.PERMISSIONS.BULK_ASSIGN(data.roleId.toString()),
      {
        method: 'POST',
        body: JSON.stringify(apiData)
      }
    );
    return response.data;
  }

  async getRolePermissions (roleId: number): Promise<PermissionDTO[]> {
    interface RolePermissionRow {
      permission_id: number;
      permission_name: string;
      permission_resource: string;
      permission_action: string;
      description?: string | null;
      granted_at: string;
    }

    const response = await this.makeRequest<ApiResponseDTO<RolePermissionRow[] | { permissions: RolePermissionRow[] }>>(
      API_ROUTES.ROLES.PERMISSIONS.LIST(roleId.toString()),
      {method: 'GET'}
    );

    const rows: RolePermissionRow[] = Array.isArray(response.data)
      ? response.data
      : (response.data?.permissions ?? []);

    // Flatten the role-permission junction shape to a Permission-like DTO
    return rows.map((row): PermissionDTO => ({
      id: row.permission_id,
      name: row.permission_name,
      resource: row.permission_resource,
      action: row.permission_action,
      description: row.description ?? undefined,
      created_at: row.granted_at,
      updated_at: row.granted_at
    }));
  }

  async getRoleUsers (roleId: number): Promise<UserDTO[]> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO[]>>(
      API_ROUTES.ROLES.USERS.LIST(roleId.toString()),
      {method: 'GET'}
    );
    return response.data ?? [];
  }

  async removeRolePermission (roleId: number, permissionId: number): Promise<void> {
    await this.makeRequest<void>(
      API_ROUTES.ROLES.PERMISSIONS.REMOVE(roleId.toString(), permissionId.toString()),
      {method: 'DELETE'}
    );
  }

  async updateRolePermission (roleId: number, permissionId: number, grantedBy: number, reason?: string): Promise<unknown> {
    const response = await this.makeRequest<ApiResponseDTO<unknown>>(
      API_ROUTES.ROLES.PERMISSIONS.UPDATE(roleId.toString(), permissionId.toString()),
      {
        method: 'PUT',
        body: JSON.stringify({
          granted_by: grantedBy,
          reason: reason || 'Updated assignment reason'
        })
      }
    );
    return response.data;
  }

  async getRolesByPermission (permissionId: number): Promise<RoleDTO[]> {
    const response = await this.makeRequest<ApiResponseDTO<RoleDTO[]>>(
      API_ROUTES.ROLES.PERMISSIONS.BY_PERMISSION(permissionId.toString()),
      {method: 'GET'}
    );
    return response.data ?? [];
  }

  async bulkRemoveRolePermissions (roleId: number, permissionIds: number[], reason?: string): Promise<void> {
    await this.makeRequest<void>(
      API_ROUTES.ROLES.PERMISSIONS.BULK_REMOVE(roleId.toString()),
      {
        method: 'DELETE',
        body: JSON.stringify({
          permission_ids: permissionIds,
          reason: reason || 'Bulk permission removal'
        })
      }
    );
  }

  async getRoleStatistics (): Promise<RoleStatistics> {
    const response = await this.makeRequest<ApiResponseDTO<RoleStatistics>>(
      API_ROUTES.ROLES.STATISTICS,
      {method: 'GET'}
    );
    return response.data;
  }

  // Additional methods for PermissionRepository
  async getPermissionById (id: number): Promise<PermissionDTO> {
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO>>(
      API_ROUTES.PERMISSIONS.BY_ID(id.toString()),
      {method: 'GET'}
    );
    return response.data;
  }

  async getUserPermissions (userId: number): Promise<PermissionDTO[]> {
    const response = await this.makeRequest<ApiResponseDTO<PermissionDTO[]>>(
      API_ROUTES.USERS.PERMISSIONS.LIST(userId.toString()),
      {method: 'GET'}
    );
    return response.data ?? [];
  }

  async removeUserPermission (userId: number, permissionId: number): Promise<void> {
    await this.makeRequest<void>(
      API_ROUTES.USERS.PERMISSIONS.REMOVE(userId.toString(), permissionId.toString()),
      {method: 'DELETE'}
    );
  }

  async getPermissionStatistics (): Promise<PermissionStatistics> {
    const response = await this.makeRequest<ApiResponseDTO<PermissionStatistics>>(
      API_ROUTES.PERMISSIONS.STATISTICS,
      {method: 'GET'}
    );
    return response.data;
  }

  // User Role Management Methods
  async getUserRoles (userId: number): Promise<RoleDTO[]> {
    const response = await this.makeRequest<ApiResponseDTO<RoleDTO[]>>(
      `/users/${userId}/roles`,
      {method: 'GET'}
    );
    return response.data ?? [];
  }

  async assignUserRole (userId: number, roleId: number, assignedBy: number): Promise<unknown> {
    const response = await this.makeRequest<ApiResponseDTO<unknown>>(
      `/users/${userId}/roles`,
      {
        method: 'POST',
        body: JSON.stringify({
          role_id: roleId,
          assigned_by: assignedBy
        })
      }
    );
    return response.data;
  }

  async updateUserRole (userId: number, roleId: number, assignedBy: number, notes?: string): Promise<unknown> {
    const response = await this.makeRequest<ApiResponseDTO<unknown>>(
      `/users/${userId}/roles/${roleId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          assigned_by: assignedBy,
          notes: notes
        })
      }
    );
    return response.data;
  }

  async removeUserRole (userId: number, roleId: number): Promise<void> {
    await this.makeRequest<void>(
      `/users/${userId}/roles/${roleId}`,
      {method: 'DELETE'}
    );
  }

  async getUsersByRole (roleId: number): Promise<UserDTO[]> {
    const response = await this.makeRequest<ApiResponseDTO<UserDTO[]>>(
      `/users/by-role/${roleId}`,
      {method: 'GET'}
    );
    return response.data ?? [];
  }
}
